<template lang="pug">
    .fabric-image-control
        canvas(ref="canvas")
        resize-sensor(@resize="resize")
</template>

<script>
import { fabric } from 'fabric'
import ScrollAccessMixin from '../../../mixins/scroll-access-mixin'

export default {
    name: 'fabric-image-control',

    mixins: [
        ScrollAccessMixin,
    ],

    props: {
        image: {
            type: String,
            required: true,
        },
        filters: {
            type: Array,
        },
        watermark: {
            type: Object,
        },
    },

    data() {
        return {
            maxWidth: null,
            watermarkOriginalSize: null,
        }
    },

    watch: {
        image() {
            // TODO: reinit image
        },
        filters() {
            this.applyFilters()
        },
    },

    methods: {
        getDataURL() {
            return new Promise((resolve) => {
                const canvasEl = document.createElement('canvas')
                this.createFabric(canvasEl, (canvas, img) => {
                    canvas.setWidth(img.width)
                    canvas.setHeight(img.height)
                    img.filters = this.filters
                    img.applyFilters()
                    canvas.renderAll()
                    resolve(canvas.toDataURL())
                })
            })
        },

        initFabric() {
            fabric.textureSize = 10000

            this.createFabric(this.$refs.canvas, (canvas) => {
                this.canvas = canvas
                this.resizeCanvas()
                this.applyFilters()
            }, true)
        },

        createFabric(canvasEl, created, applyWatermark) {
            const canvas = new fabric.StaticCanvas(canvasEl)

            const promises = []
            let mainImg = null

            promises.push(this.loadImageFromUrlAsync(this.image).then(img => {
                canvas.add(img)
                mainImg = img
            }))
            if (applyWatermark && this.watermark) {
                promises.push(this.loadImageFromUrlAsync(this.watermark.url).then(img => {
                    canvas.add(img)
                    this.watermarkOriginalSize = {
                        width: img.width,
                        height: img.height,
                    }
                }))
            }

            Promise.all(promises).then(() => {
                created(canvas, mainImg)
            })
        },

        loadImageFromUrlAsync(url) {
            return new Promise(resolve => {
                fabric.Image.fromURL(url, img => resolve(img))
            })
        },

        resizeCanvas() {
            if (!this.canvas) {
                return
            }

            const img = this.canvas.item(0)

            if (!img) {
                return
            }

            let canvasW = img.width
            let canvasH = img.height
            
            if (canvasW > this.maxWidth) {
                const scale = this.maxWidth / canvasW

                canvasW *= scale
                canvasH *= scale
                img.scale(scale)
            }

            if (this.watermark) {
                const watermarkImg = this.canvas.item(1)
                const futureDimensions = this.getWatermarkDimensions({ width: canvasW, height: canvasH }, this.watermarkOriginalSize, this.watermark)

                const scale = futureDimensions.width / watermarkImg.width

                watermarkImg.scale(scale)
                watermarkImg.set('left', futureDimensions.left)
                watermarkImg.set('top', futureDimensions.top)
                watermarkImg.setCoords()
            }

            this.canvas.setWidth(canvasW)
            this.canvas.setHeight(canvasH)

            this.canvas.renderAll()

            this.resetScroll()
        },

        getWatermarkDimensions(imageSize, watermarkSize, watermarkOptions) {
            const imageWidth = imageSize.width
            const imageHeight = imageSize.height

            const ratio = watermarkSize.width / imageWidth
            const desiredRatio = watermarkOptions.size
            const ratioDiffFactor = desiredRatio / ratio

            const width = watermarkSize.width * ratioDiffFactor
            const height = watermarkSize.height * ratioDiffFactor
            const gutter = imageSize.width * watermarkOptions.gutter

            const [positionH, positionV] = watermarkOptions.position.split(' ')
            let left, top

            switch (positionH) {
                case 'left':
                    left = gutter
                    break
                case 'center':
                    left = (imageWidth - width) / 2
                    break
                case 'right':
                    left = imageWidth - width - gutter
                    break
            }

            switch (positionV) {
                case 'top':
                    top = gutter
                    break
                case 'center':
                    top = (imageHeight - height) / 2
                    break
                case 'bottom':
                    top = imageHeight - height - gutter
                    break
            }

            return {
                width,
                height,
                left,
                top,
            }
        },

        applyFilters() {
            if (!this.canvas) {
                return
            }

            const img = this.canvas.item(0)
            img.filters = this.filters

            if (this.filters && this.filters.length) {
                this.$emit('applying')

                setTimeout(() => {
                    img.applyFilters()
                    this.canvas.renderAll()

                    this.$emit('applied')
                }, 25)
            }
            else {
                img.applyFilters()
                this.canvas.renderAll()
            }
        },

        resize(e) {
            this.maxWidth = e.width
            this.resizeCanvas()
        },

        resetScroll() {
            this.getSimplebar().recalculate()
        },
    },

    mounted() {
        this.initFabric()
    },

    beforeDestroy() {
        if (this.canvas) {
            this.canvas.dispose()
        }
    },
}
</script>
